/*
  DORVETTI — Carrito lateral (MOV-029). Acompaña a sections/dorvetti-cart-drawer.liquid.
  - [data-dv-cart-open] abre el panel (el icono de la bolsa); sin JS, el enlace va a /cart.
  - Cantidades, quitar, «Add a little more» y códigos de descuento por la Ajax API de Shopify con
    Section Rendering: cada respuesta trae el HTML nuevo de la sección y se sustituye el cuerpo.
  - Se abre solo al añadir: evento dv:cart:added (carrusel) o el estándar shopify:cart:lines-update.
  - Maqueta (?maqueta=1, localStorage «dv-maqueta»): con la bolsa vacía, bolsa de ejemplo inerte.
  Foco atrapado dentro del panel, Escape y clic fuera cierran, y el foco vuelve a donde estaba.
*/
(() => {
  'use strict';
  // Ojo: window.DvCart es el propio <div id="DvCart"> (acceso por id); la API se llama DvBag.
  if (window.DvBag) return;

  const SECTION = 'dorvetti-cart-drawer';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let lastFocus = null;
  let busy = false;
  let view = 'bag';

  const drawer = () => document.getElementById('DvCart');
  const panel = () => drawer()?.querySelector('.dv-cart__panel');

  const maquetaOn = () => {
    try {
      return window.localStorage.getItem('dv-maqueta') === '1';
    } catch (error) {
      return false;
    }
  };

  const setCount = (count) => {
    document.querySelectorAll('[data-dv-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  };

  // Con la bolsa vacía y la maqueta activa, se ve la bolsa de ejemplo del <template>.
  const applyMaqueta = () => {
    const root = drawer();
    if (!root || Number(root.dataset.count) > 0 || !maquetaOn()) return;
    const template = root.querySelector('template[data-dv-cart-maqueta]');
    if (!template) return;
    const fragment = template.content.cloneNode(true);
    ['[data-dv-cart-body]', '[data-dv-cart-foot]'].forEach((selector) => {
      const fresh = fragment.querySelector(selector);
      const current = root.querySelector(selector);
      if (fresh && current) current.replaceWith(fresh);
    });
    root.classList.add('is-maqueta');
  };

  const render = (html) => {
    const root = drawer();
    if (!root || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById('DvCart');
    if (!fresh) return;
    ['[data-dv-cart-body]', '[data-dv-cart-foot]'].forEach((selector) => {
      const next = fresh.querySelector(selector);
      const current = root.querySelector(selector);
      if (next && current) current.replaceWith(next);
    });
    root.querySelector('template[data-dv-cart-maqueta]')?.remove();
    const template = fresh.querySelector('template[data-dv-cart-maqueta]');
    if (template) root.appendChild(template);
    root.classList.remove('is-maqueta');
    root.dataset.count = fresh.dataset.count;
    setCount(Number(fresh.dataset.count) || 0);
    applyMaqueta();
    if (!root.hidden) showView(view);
    if (window.DvWishlist) window.DvWishlist.sync();
  };

  const refresh = async () => {
    const response = await fetch(`${ROOT}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
    const json = await response.json();
    render(json[SECTION]);
  };

  const post = async (path, body) => {
    const response = await fetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname })
    });
    const json = await response.json();
    if (!response.ok) throw json;
    if (json.sections && json.sections[SECTION]) render(json.sections[SECTION]);
    else await refresh();
    return json;
  };

  // Mensaje breve bajo el código, o aviso flotante en la maqueta.
  const say = (text, anchor) => {
    const root = drawer();
    if (!root || !text) return;
    const msg = anchor?.closest('.dv-cart__discount')?.querySelector('[data-dv-discount-msg]');
    if (msg) {
      msg.textContent = text;
      msg.hidden = false;
      return;
    }
    let toast = root.querySelector('.dv-cart__toast');
    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'dv-cart__toast';
      toast.setAttribute('role', 'status');
      panel()?.appendChild(toast);
    }
    toast.textContent = text;
    toast.classList.add('is-visible');
    window.clearTimeout(toast._timer);
    toast._timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const focusables = () =>
    [...(panel()?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])].filter(
      (el) => el.offsetParent !== null
    );

  const showView = (next) => {
    const root = drawer();
    if (!root) return;
    view = next === 'wish' ? 'wish' : 'bag';
    const wish = view === 'wish';
    root.querySelector('[data-dv-cart-body]').hidden = wish;
    root.querySelector('[data-dv-cart-foot]').hidden = wish;
    root.querySelector('[data-dv-cart-wish]').hidden = !wish;
    root.querySelectorAll('[data-dv-cart-view]').forEach((button) => {
      const on = button.dataset.dvCartView === view;
      button.classList.toggle('is-active', on);
      button.setAttribute('aria-pressed', String(on));
    });
    if (wish) renderWishlist();
  };

  // Favoritos dentro del panel (solo con sesión; sin sesión, el panel invita a entrar).
  const renderWishlist = async () => {
    const root = drawer();
    const list = root?.querySelector('[data-dv-cart-wish-list]');
    if (!list || !window.DvWishlist) return;
    const handles = window.DvWishlist.read();
    const saved = window.DvWishlist.readItems();
    const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const rows = await Promise.all(
      handles.slice(0, 20).map(async (handle) => {
        try {
          const response = await fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const product = await response.json();
            return { handle, name: product.title.split(' — ')[0], url: product.url, img: product.featured_image || '' };
          }
        } catch (error) {
          /* pieza sin publicar: se usa la ficha guardada */
        }
        const item = saved[handle];
        return item ? { handle, name: item.name, desc: item.desc, img: item.img } : null;
      })
    );
    const found = rows.filter(Boolean);
    list.innerHTML = found
      .map(
        (row) => `<li class="dv-cart__wish-item">
          <span class="dv-cart__wish-media">${/^(https:)?\/\//.test(String(row.img || '')) ? `<img src="${esc(row.img)}" alt="" loading="lazy">` : ''}</span>
          <span class="dv-cart__wish-info">
            ${row.url ? `<a class="dv-cart__item-title" href="${esc(row.url)}">${esc(row.name)}</a>` : `<span class="dv-cart__item-title">${esc(row.name)}</span>`}
            ${row.desc ? `<span class="dv-cart__item-variant">${esc(row.desc)}</span>` : ''}
          </span>
        </li>`
      )
      .join('');
    root.querySelector('[data-dv-cart-wish-empty]').hidden = found.length > 0;
  };

  const open = (view = 'bag') => {
    const root = drawer();
    if (!root) return false;
    if (!root.hidden && root.classList.contains('is-open')) {
      showView(view);
      return true;
    }
    lastFocus = document.activeElement;
    applyMaqueta();
    root.hidden = false;
    showView(view);
    document.documentElement.classList.add('dv-cart-open');
    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      panel()?.focus({ preventScroll: true });
    });
    return true;
  };

  const close = () => {
    const root = drawer();
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('dv-cart-open');
    const done = () => {
      root.hidden = true;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    };
    if (reduceMotion.matches) done();
    else window.setTimeout(done, 420);
  };

  const withBusy = async (button, task) => {
    if (busy) return;
    busy = true;
    drawer()?.classList.add('is-busy');
    if (button) button.disabled = true;
    try {
      await task();
    } catch (error) {
      say((error && error.description) || drawer()?.dataset.error);
    } finally {
      busy = false;
      drawer()?.classList.remove('is-busy');
      if (button && button.isConnected) button.disabled = false;
    }
  };

  const currentCodes = () =>
    (drawer()?.querySelector('.dv-cart__discount')?.dataset.codes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dv-cart-open]');
    if (opener) {
      if (open('bag')) event.preventDefault();
      return;
    }
    const root = drawer();
    if (!root || !root.contains(event.target)) return;

    if (event.target.closest('[data-dv-cart-close]')) {
      close();
      return;
    }
    const viewButton = event.target.closest('[data-dv-cart-view]');
    if (viewButton) {
      showView(viewButton.dataset.dvCartView);
      return;
    }
    const demo = event.target.closest('[data-dv-demo]');
    if (demo) {
      event.preventDefault();
      say(root.dataset.demoNote);
      return;
    }
    const qty = event.target.closest('[data-dv-qty]');
    if (qty) {
      const line = Number(qty.closest('[data-line]')?.dataset.line);
      const quantity = Math.max(0, Number(qty.dataset.dvQty));
      withBusy(qty, () => post('cart/change.js', { line, quantity }));
      return;
    }
    const add = event.target.closest('[data-dv-cart-add]');
    if (add) {
      withBusy(add, () => post('cart/add.js', { items: [{ id: Number(add.dataset.dvCartAdd), quantity: 1 }] }));
      return;
    }
    const removeCode = event.target.closest('[data-dv-code-remove]');
    if (removeCode) {
      const codes = currentCodes().filter((code) => code !== removeCode.dataset.dvCodeRemove);
      withBusy(removeCode, () => post('cart/update.js', { discount: codes.join(',') }));
    }
  });

  document.addEventListener('submit', (event) => {
    const root = drawer();
    if (!root || !root.contains(event.target)) return;
    if (event.target.matches('[data-dv-demo-form]')) {
      event.preventDefault();
      say(root.dataset.demoNote);
      return;
    }
    if (!event.target.matches('[data-dv-discount-form]')) return;
    event.preventDefault();
    const form = event.target;
    const input = form.querySelector('input[name="discount"]');
    const code = (input?.value || '').trim();
    if (!code) return;
    const previous = currentCodes();
    if (previous.some((c) => c.toLowerCase() === code.toLowerCase())) return;
    withBusy(form.querySelector('button'), async () => {
      const cart = await post('cart/update.js', { discount: [...previous, code].join(',') });
      const entry = (cart.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
      if (!entry || !entry.applicable) {
        // No vale para esta bolsa: se retira y se avisa.
        await post('cart/update.js', { discount: previous.join(',') });
        const msgAnchor = drawer()?.querySelector('[data-dv-discount-form]');
        say(root.dataset.codeError, msgAnchor);
        const field = drawer()?.querySelector('[data-dv-discount-form] input');
        if (field) field.value = code;
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    const root = drawer();
    if (!root || root.hidden) return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel())) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Al añadir desde el carrusel (dv:cart:added) o desde un formulario de producto del tema.
  document.addEventListener('dv:cart:added', () => {
    refresh()
      .then(() => open('bag'))
      .catch(() => {});
  });
  document.addEventListener('dv:cart:open', () => open('bag'));
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const promise = event.promise || event.detail?.promise;
    const after = () =>
      refresh()
        .then(() => {
          if (event.action === 'add' || event.detail?.action === 'add') open('bag');
        })
        .catch(() => {});
    if (promise && promise.then) promise.then(after, () => {});
    else after();
  });
  document.addEventListener('dv:wishlist', () => {
    if (!drawer()?.querySelector('[data-dv-cart-wish]')?.hidden) renderWishlist();
  });

  // Acción estándar de Shopify «abrir carrito» (la usan los formularios del tema): abre este panel.
  const hookActions = () => {
    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || !actions.openCart || typeof actions.openCart.configure !== 'function') return;
    actions.openCart.configure({
      async handler() {
        await refresh().catch(() => {});
        if (!open('bag')) window.location.href = `${ROOT}cart`;
      }
    });
  };
  window.addEventListener('load', hookActions, { once: true });

  window.DvBag = { open, close, refresh };
})();
